import { CSSProperties, useEffect, useRef, useState } from 'react';
import {
    CheckboxFormFieldComponent,
    AdvancedSelectFormFieldComponent,
    LinkFieldComponent,
    MultiSelectDropdownFormFieldComponent,
    TextAreaFormFieldComponent,
    TextFormFieldComponent,
    TranslationComponent
} from '@vivli/shared/components';
import { IDropdownMenuItem } from '@vivli/shared/infrastructure/interface';
import { EnquiryDiscussionGridComponent } from './enquiry-discussion-grid.component';
import { IDataInfoRequest, IEnquiry, IEnquiryOrganizations } from '@vivli/features/enquiry/infastructure/interface';
import { DataTypeOptions, DirDropDownConstants } from '@vivli/features/enquiry/infastructure/constants';
import { useEnquiryContext } from '@vivli/features/enquiry/infastructure/context';
import { first } from 'rxjs/operators';
import { Color, Size, Styles } from '@vivli/shared/theme';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import {
    useActiveUserContext,
    useEnquiryApiContext,
    useModalApiContext,
    useMyDataRequestsApiContext
} from '@vivli/core/infrastructure/context';
import { DirStatusEnum, EnquiryStatusEnum } from '@vivli/features/enquiry/infastructure/enum';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { AssetsConstant, DTICommonConst } from '@vivli/shared/infrastructure/constants';
import { AdvancedDropdownMenuComponent } from '@vivli/features/search/components';
import { ResponseHint } from './response-hint';
import { ReasonHint } from './reason-hint';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useDoiTransform } from '@vivli/features/studies/infrastructure/hook';
import { ButtonView } from '@vivli/shared/views';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

const flexStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center'
};
const flexStyleColumn: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column'
};
const twoColumnGrid: CSSProperties = {
    display: 'grid',
    justifyContent: 'space-around',
    gridTemplateColumns: '1fr 3fr',
    columnGap: '20px'
};
const twoColumnInnerGrid: CSSProperties = {
    display: 'grid',
    justifyContent: 'space-around',
    gridTemplateColumns: '3fr 1fr',
    columnGap: '0px'
};
const threeColumnGrid: CSSProperties = {
    display: 'grid',
    justifyContent: 'space-around',
    gridTemplateColumns: '1fr 2fr 1fr',
    columnGap: '25px'
};
const fourColumnGrid: CSSProperties = {
    display: 'grid',
    justifyContent: 'space-around',
    gridTemplateColumns: '20px 1fr 2fr 1fr',
    columnGap: '20px'
};

const sixColumnGrid: CSSProperties = {
    display: 'grid',
    justifyContent: 'left',
    gridTemplateColumns: '25px 13% 50% 18% 1fr 5%',
    columnGap: '15px'
};
const paddingTop20: CSSProperties = {
    paddingTop: '20px'
};

const paddingTop35: CSSProperties = {
    paddingTop: '35px'
};
const paddingTop17: CSSProperties = {
    paddingTop: '17px'
};
const paddingTop5: CSSProperties = {
    paddingTop: '5px'
};
const dirContainer: CSSProperties = {
    padding: '20px',
    backgroundColor: '#DDDDDD', //'#f5f5f5',
    border: '3px solid #c3bcbc',
    marginTop: '30px',
    height: 'auto'
};

const dirContainerStyle: CSSProperties = {
    display: 'grid',
    gridAutoRows: 'auto',
    rowGap: '20px'
};

const marginTop20: CSSProperties = {
    marginTop: '20px'
};
const marginTop17: CSSProperties = {
    marginTop: '17px'
};
const linkRegistryUrlStyle: CSSProperties = {
    backgroundColor: 'none',
    boxShadow: 'none',
    borderBottom: 'none',
    flexDirection: 'row',
    fontSize: '16px',
    paddingTop: '0px',
    marginTop: '0px'
};
const iconStyle: CSSProperties = {
    fontSize: '1.5em',
    padding: '0px 0px 0px 0px',
    color: Color.TEXT_LIGHT_BLUE,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: '15px',
    cursor: 'pointer'
};
const isVivliStyle: CSSProperties = {
    color: '#ff0000',
    paddingTop: '20px'
};
const isVivliLinkStyle: CSSProperties = {
    color: Color.VIVLI_LINK_BLUE,
    backgroundColor: 'none',
    cursor: 'pointer',
    paddingTop: '20px',
    textDecoration: 'underline'
};
const smallButtonStyle = {
    height: 25,
    color: Color.DARK_GRAY,
    backgroundColor: '#DDDDDD',
    fontSize: Size.FontSize.XLarge,
    width: 25,
    minWidth: 25,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    ':active': { transform: 'scale(0.98)' },
    ':hover': { opacity: Size.HOVER_OPACITY },
    transition: `scale 0.35s, opacity ${Size.HOVER_TRANSITION_TIME}s`
};
const smallButtonStyleError = {
    height: 25,
    color: Color.ERROR_RED,
    backgroundColor: '#DDDDDD',
    fontSize: Size.FontSize.XLarge,
    width: 25,
    minWidth: 25,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    ':active': { transform: 'scale(0.98)' },
    ':hover': { opacity: Size.HOVER_OPACITY },
    transition: `scale 0.35s, opacity ${Size.HOVER_TRANSITION_TIME}s`
};
const dropDownBtnStyle: CSSProperties = {
    backgroundImage: `url(${AssetsConstant.DOWN_CHEVRON_WHITE})`,
    paddingRight: Size.PADDING,
    fontWeight: Size.FontWeight.SemiBold,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 20px center',
    backgroundSize: '20px',
    marginBottom: '15px',
    marginRight: '0',
    width: '100px'
};

interface EnquiryDataInfoRequestComponentProps {
    dirIndex: number;
    onRemove: (dirIndex: number) => void;
    onUpdate: (dirIndex: number, dir: IDataInfoRequest) => void;
}

export const EnquiryDataInfoRequestComponent = ({ dirIndex, onRemove, onUpdate }: EnquiryDataInfoRequestComponentProps) => {
    const {
        handleGetStudy,
        setNotify,
        getAddDirStudy,
        isLoading,
        toggleDir,
        isDirOpen,
        setDirAsClosed,
        addDirRef,
        removeDirRef,
        initialGetStudy,
        getStudyLinkBySponsor,
        enquiry
    } = useEnquiryContext();
    const enquiryService = useEnquiryApiContext();
    const modalService = useModalApiContext();
    const { user, orgHasPermission, hasPermission } = useActiveUserContext();
    const dataInfoRequest = useWatch({ name: `dataInfoRequests.${dirIndex}` });
    const formApi = useFormContext<IEnquiry>();
    const { errors } = useFormState();
    const currentStatus = enquiry?.status ?? EnquiryStatusEnum.Draft;
    const { myDataRequests } = useMyDataRequestsApiContext();
    const [enquiryOrganizations, setEnquiryOrganizations] = useState<IEnquiryOrganizations[]>();
    const [studyReadOnly, setStudyReadOnly] = useState<boolean>(false);
    const [nctIdHasValue, setNctIdHasValue] = useState<boolean>(false);
    const [reasonReadOnly, setReasonReadOnly] = useState<boolean>(false);
    const [responseReadOnly, setResponseReadOnly] = useState<boolean>(false);
    const [isRequestStudyMenuOpen, setIsRequestStudyMenuOpen] = useState(false);
    const reasonLabel = DirDropDownConstants.NotAvailableReasonOptions;
    const dataRequestedLabel = DirDropDownConstants.DataTypeOptions;
    const dirStatusLabel = DirDropDownConstants.DirStatusOptions;
    const dirRef = useRef<HTMLDivElement>();
    const { transformDoiUrl } = useDoiTransform();

    const [SavedStatus, SetSavedStatus] = useState<number>(-1);
    const [displayRequestStudyBtn, setDisplayRequestStudyBtn] = useState<boolean>(false);
    const [lastNctOrSponsorId, setLastNctOrSponsorId] = useState(dataInfoRequest.nctId || dataInfoRequest.sponsorId);

    const reasonMenuItems = Object.keys(reasonLabel).map<IDropdownMenuItem>((key) => {
        return {
            title: reasonLabel[key],
            value: Number(key)
        };
    });
    const dataRequestedMenuItems = dataRequestedLabel.map(({ key, value }) => ({ title: value, value: Number(key) }));
    const dirStatusMenuItems = Object.keys(dirStatusLabel).map<IDropdownMenuItem>((key) => {
        return {
            title: dirStatusLabel[key],
            value: Number(key)
        };
    });
    const organizationMenuItems = enquiryOrganizations?.map<IDropdownMenuItem>(({ valueField, keyField }) => {
        return {
            title: valueField,
            value: keyField
        };
    });
    const isRequester = enquiry?.requesterId === user.id;
    //TODO PERMFE - redo with permissions+
    const isOrgAdmin = orgHasPermission(dataInfoRequest.orgId, PermissionsEnum.EnqProviderAct);
    const isOrgMember = user.orgMemberships.some((x) => x.orgId === dataInfoRequest.orgId && x.isEnabled);
    const isVivliAdmin = hasPermission(PermissionsEnum.EnqActAdv);
    const isAdmin = isVivliAdmin || isOrgAdmin;
    const isInReview = currentStatus === EnquiryStatusEnum.Review;
    const isApprovedState = isInReview || currentStatus === EnquiryStatusEnum.Withdrawn || currentStatus === EnquiryStatusEnum.Archived;
    const isDynamicReadOnly =
        (!isVivliAdmin && currentStatus == EnquiryStatusEnum.EnquiryValidation) ||
        (user.id !== enquiry?.requesterId && !isVivliAdmin && currentStatus == EnquiryStatusEnum.Draft) ||
        isApprovedState;
    const isArchivedOrWithdrawn = currentStatus === EnquiryStatusEnum.Withdrawn || currentStatus === EnquiryStatusEnum.Archived;

    const orgAdminReadOnlyState =
        isOrgAdmin && (currentStatus === EnquiryStatusEnum.Withdrawn || currentStatus === EnquiryStatusEnum.Archived);

    //in review status - show Notify checkbox to admin, enquirer and DC for the DIR organization
    //in draft or form validation status - to admin and enquirer
    //TODO PERMFE - redo with permissions+
    const showNotify =
        (isInReview && (isVivliAdmin || isRequester || isOrgMember)) ||
        ((currentStatus === EnquiryStatusEnum.Draft || currentStatus === EnquiryStatusEnum.EnquiryValidation) &&
            (isVivliAdmin || isRequester)) ||
        (isAdmin && isArchivedOrWithdrawn);
    const displayPreviousEnqLink = (isInReview || currentStatus === EnquiryStatusEnum.EnquiryValidation) && (isVivliAdmin || isOrgMember);
    //VivliAdmin always can edit the title - #11444
    //TODO PERMFE - redo with permissions+
    const titleReadOnly = !isVivliAdmin && (!!formApi.getValues().dataInfoRequests[dirIndex].registryUrl || isDynamicReadOnly);
    const hasError = formApi.getFieldState(`dataInfoRequests.${dirIndex}`).invalid;
    const isReadOnlyRequester = currentStatus !== EnquiryStatusEnum.Draft && isRequester;
    const isSponsorReadOnly = studyReadOnly && !(isInReview && isAdmin);
    const nctIsEditable =
        !studyReadOnly || (currentStatus === EnquiryStatusEnum.Review && (isVivliAdmin || (!nctIdHasValue && isOrgAdmin)));
    const timerRef = useRef<NodeJS.Timeout | null>();
    const nctIdValue = useWatch({ name: `dataInfoRequests.${dirIndex}.nctId` });

    // Errors had to be fetched in this manner.
    const nctFieldHasError = errors?.dataInfoRequests?.[dirIndex]?.nctId;

    const handleFailure = (err: string) => {
        modalService.error(`An error occurred retrieving the Organizations. Please try again or contact Vivli support. Message: ${err}`);
    };

    const handleGetNctSponsorIdStudy = (e) => {
        const nctOrSponsorId = e.target.value.trim();
        if (nctOrSponsorId !== '' && nctOrSponsorId !== lastNctOrSponsorId) {
            setLastNctOrSponsorId(nctOrSponsorId);
            handleGetStudy(nctOrSponsorId, dirIndex);
        } else if (nctOrSponsorId === '' && nctIdValue == '' && dataInfoRequest.sponsorId == '') {
            formApi.setValue(`dataInfoRequests.${dirIndex}.registryUrl`, '');
            formApi.setValue(`dataInfoRequests.${dirIndex}.isVivliPosted`, false);
            formApi.setValue(`dataInfoRequests.${dirIndex}.studyCompletionDate`, '');
            formApi.setValue(`dataInfoRequests.${dirIndex}.sponsor`, '');
            formApi.trigger();
            setLastNctOrSponsorId('NoValue');
        }
    };

    const handleGetNctIdStudyCustom = () => {
        const nctFieldState = formApi.getFieldState(`dataInfoRequests.${dirIndex}.nctId`);
        if (nctIdValue !== '' && nctIdValue !== lastNctOrSponsorId) {
            if (nctFieldState?.invalid) {
                return;
            }
            setLastNctOrSponsorId(nctIdValue);
            handleGetStudy(nctIdValue, dirIndex);
        }
    };
    const handleGetNctIdStudy = (e) => {
        const nctOrSponsorId = e.target.value.trim();
        const nctFieldState = formApi.getFieldState(`dataInfoRequests.${dirIndex}.nctId`);
        if (nctOrSponsorId !== '' && nctOrSponsorId !== lastNctOrSponsorId) {
            if (nctFieldState?.invalid) {
                //do not do a lookup if NCT Id is invalid
                return;
            }
            handleGetNctSponsorIdStudy(e);
        }
    };

    const handleDebounce = (delay = 500) => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            handleGetNctIdStudyCustom();
        }, delay);
    };

    const handleKeyUpNctIdGetStudy = (e) => {
        if (e.key === 'Enter') {
            handleGetNctIdStudy(e);
        }
    };

    const handleKeyUpGetStudy = (e) => {
        if (e.key === 'Enter') {
            handleGetNctSponsorIdStudy(e);
        }
    };
    const handleDeleteDirStudy = () => {
        const confirmMsg = `Are you sure you want to remove this study from the enquiry?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                onRemove(dirIndex);
                setDirAsClosed(dirIndex); // remove from "openDirs" array
            }
        });
    };
    const getOrgName = () => {
        var orgs = enquiryOrganizations?.filter((o) => o.keyField === dataInfoRequest.orgId);
        if (orgs?.length > 0) {
            return orgs[0].valueField;
        }
        return '';
    };

    const getEnquiriesReportLink = (nctId) => {
        return `/admin/report?reportType=Enquiry&nctId=${nctId}`;
    };

    const addToCart = (selectedItem?: IDropdownMenuItem) => {
        const isNewRequest = selectedItem.value === 'addnewrequest';
        const receivedTitle = isNewRequest ? 'New Research Data Request' : selectedItem.title;
        getAddDirStudy(enquiry?.id, dataInfoRequest.id, receivedTitle, isNewRequest);
        setIsRequestStudyMenuOpen(false);
    };

    const handleSelectMenuOutsideClick = () => {
        setIsRequestStudyMenuOpen(false);
    };

    const dropDownStudyItems = (myDataRequests || [])
        .filter((dr) => dr.userId === user.id && dr.status === DataRequestStatusEnum.Draft)
        .map(
            (dataRequest) =>
                ({
                    title: dataRequest.requestTitle || dataRequest.id,
                    value: dataRequest.id,
                    dataRequest
                }) as IDropdownMenuItem
        )
        .concat({
            title: '+ Add New Request',
            value: 'addnewrequest'
        } as IDropdownMenuItem);

    useEffect(() => {
        enquiryService
            .getEnquiryOrganizations()
            .pipe(first())
            .subscribe((data) => {
                setEnquiryOrganizations(data);
            }, handleFailure);

        if (isDynamicReadOnly) {
            setStudyReadOnly(true);
        }

        if (
            //TODO PERMFE - redo with permissions+
            !orgHasPermission(dataInfoRequest.orgId, PermissionsEnum.ContribChat) &&
            !isVivliAdmin
        ) {
            setReasonReadOnly(true);
            setResponseReadOnly(true);
        }

        if (
            //TODO PERMFE - redo with permissions+
            (dataInfoRequest.status === DirStatusEnum.AvailableUnlisted || dataInfoRequest.status === DirStatusEnum.AvailableListed) &&
            (isRequester || isVivliAdmin) &&
            (currentStatus === EnquiryStatusEnum.Review || currentStatus == EnquiryStatusEnum.Archived)
        ) {
            setDisplayRequestStudyBtn(true);
        }
        setNctIdHasValue(!!dataInfoRequest.nctId);
        getVivliStudyLink();
    }, []);

    useEffect(() => {
        if (!dataInfoRequest.studyTitle) {
            formApi.trigger([`dataInfoRequests`]);
        }
    }, [dataInfoRequest.studyTitle]);

    useEffect(() => {
        if (dataInfoRequest.status) {
            if (SavedStatus != -1 && SavedStatus != dataInfoRequest.status) {
                setNotify(dataInfoRequest, dirIndex);
            }
            SetSavedStatus(dataInfoRequest.status);
        }
        handleResponseWarning();
        formApi.trigger([`dataInfoRequests`]);
    }, [dataInfoRequest.status]);

    useEffect(() => {
        if (!nctFieldHasError && nctIdValue && nctIdValue.length > 1) {
            handleDebounce();
        }
    }, [nctIdValue, nctFieldHasError]);

    useEffect(() => {
        addDirRef(dirRef, dirIndex);

        return () => {
            removeDirRef(dirIndex);
        };
    }, []);

    const handleResponseWarning = () => {
        const dataInfoRequests = formApi.getValues('dataInfoRequests');
        const currentDataInfoRequest = dataInfoRequests[dirIndex];

        const confirmMsg = `Please provide a Sponsor ID so this study can be requested in a data request.
        If your study is listed on clinicaltrials.gov, please provide the sponsor ID associated with that listing.
        If it is not listed on clinicaltrials.gov, please provide an internal sponsor ID.
         Once you've added the Sponsor ID, please click the “Save & Notify” button on top.`;

        if (
            currentDataInfoRequest.sponsorId.length < 1 &&
            (currentDataInfoRequest.status === DirStatusEnum.AvailableUnlisted ||
                currentDataInfoRequest.status === DirStatusEnum.AvailableListed)
        ) {
            modalService.error(confirmMsg);

            dataInfoRequest.status = SavedStatus;
            onUpdate(dirIndex, dataInfoRequest);
        }
    };
    const getVivliStudyLink = () => {
        if (!dataInfoRequest) {
            return;
        }
        if (dataInfoRequest.isVivliPosted) {
            initialGetStudy(dataInfoRequest.sponsorId, dirIndex);
        }
    };

    return (
        <div style={dirContainer} ref={dirRef}>
            {!isDirOpen(dirIndex) && (
                <div style={sixColumnGrid}>
                    <div>
                        <ButtonView
                            title="Open full view"
                            style={hasError ? smallButtonStyleError : smallButtonStyle}
                            onClick={() => toggleDir(dirIndex)}
                        >
                            +
                        </ButtonView>
                    </div>
                    <div style={paddingTop5}>
                        <strong>NCT ID:</strong> {dataInfoRequest.nctId}
                    </div>
                    <div style={paddingTop5}>
                        <strong>Study Title:</strong> {dataInfoRequest.studyTitle}
                    </div>
                    <div style={paddingTop5}>
                        <strong>Data Contributor:</strong> {getOrgName()}
                    </div>
                    <div style={paddingTop5}>
                        <strong>Status:</strong> {dataInfoRequest.statusString}
                    </div>
                    {hasError && <div>❗</div>}
                </div>
            )}
            {isDirOpen(dirIndex) && (
                <div style={dirContainerStyle}>
                    <div>
                        {currentStatus === EnquiryStatusEnum.Draft && (
                            <div style={{ paddingBottom: '15px', paddingLeft: '40px' }}>
                                Please enter an NCT Id or Sponsor Id if the study is on clinicaltrials.gov, or enter the study title.
                            </div>
                        )}
                        {displayRequestStudyBtn && (
                            <div>
                                <AdvancedDropdownMenuComponent
                                    items={dropDownStudyItems}
                                    onClickOutside={() => handleSelectMenuOutsideClick()}
                                    onItemClick={(item) => addToCart(item)}
                                >
                                    <ButtonView
                                        isLoading={isLoading}
                                        style={
                                            {
                                                ...Styles.Button.BUTTON_VIVLI_BLUE,
                                                backgroundColor: isRequestStudyMenuOpen ? Color.VIVLI_DARK_BLUE : Color.VIVLI_LIGHT_BLUE,
                                                ...dropDownBtnStyle,
                                                ':active': 'inherit'
                                            } as CSSProperties
                                        }
                                        className="request_study_menu"
                                        data-test-id={DTICommonConst.RequestStudyMenu}
                                    >
                                        Request{' '}
                                        <TranslationComponent
                                            style={{
                                                textTransform: 'capitalize'
                                            }}
                                        >
                                            study
                                        </TranslationComponent>
                                    </ButtonView>
                                </AdvancedDropdownMenuComponent>
                            </div>
                        )}
                    </div>
                    <div style={fourColumnGrid}>
                        <div>
                            <ButtonView title="Close full view" style={smallButtonStyle} onClick={() => toggleDir(dirIndex)}>
                                -
                            </ButtonView>
                        </div>
                        <div>
                            <div style={displayPreviousEnqLink ? twoColumnInnerGrid : null}>
                                <TextFormFieldComponent
                                    name={`dataInfoRequests.${dirIndex}.nctId`}
                                    label={'NCT ID'}
                                    onBlur={(e) => handleGetNctSponsorIdStudy(e)}
                                    onKeyDown={(e) => handleKeyUpNctIdGetStudy(e)}
                                    readonly={!nctIsEditable}
                                ></TextFormFieldComponent>

                                {displayPreviousEnqLink && (
                                    <LinkFieldComponent
                                        linkTitle={'Previous Enquiries'}
                                        tooltip={'See the list of enquiries associated to this Study.'}
                                        linkContent={getEnquiriesReportLink(dataInfoRequest?.nctId)}
                                        label={''}
                                        style={{ ...linkRegistryUrlStyle, marginLeft: '0px' }}
                                        linkStyle={{ fontSize: '14px' }}
                                    />
                                )}
                            </div>
                            <div style={{ ...flexStyleColumn, ...paddingTop17 }}>OR</div>
                            <TextFormFieldComponent
                                name={`dataInfoRequests.${dirIndex}.sponsorId`}
                                label={'Sponsor ID'}
                                style={marginTop17}
                                onBlur={(e) => handleGetNctSponsorIdStudy(e)}
                                onKeyDown={(e) => handleKeyUpGetStudy(e)}
                                readonly={isSponsorReadOnly}
                            ></TextFormFieldComponent>
                        </div>
                        <div>
                            <TextAreaFormFieldComponent
                                name={`dataInfoRequests.${dirIndex}.studyTitle`}
                                label={'Study Title'}
                                readonly={titleReadOnly}
                            ></TextAreaFormFieldComponent>
                            <div></div>
                        </div>

                        <div>
                            {showNotify && (
                                <CheckboxFormFieldComponent
                                    name={`dataInfoRequests.${dirIndex}.notify`}
                                    label={'Notify on "Save & Notify":'}
                                    truncateLabel={false}
                                    reverseOrientation={true}
                                />
                            )}
                            {
                                //TODO PERMFE - redo with permissions (and isVivliAdmin below in AdvancedSelectFormFieldComponent)+
                                currentStatus === EnquiryStatusEnum.Draft && (isVivliAdmin || isRequester) && (
                                    <FontAwesomeIcon
                                        aria-hidden="true"
                                        style={iconStyle}
                                        onClick={() => handleDeleteDirStudy()}
                                        icon={faTrash}
                                    />
                                )
                            }
                            <AdvancedSelectFormFieldComponent
                                label={'Data Contributor'}
                                name={`dataInfoRequests.${dirIndex}.orgId`}
                                items={organizationMenuItems}
                                readonly={(isApprovedState && !isVivliAdmin) || isReadOnlyRequester}
                            />
                            <div style={paddingTop20}>Sponsor: {dataInfoRequest?.sponsor}</div>
                        </div>
                    </div>
                    <div style={fourColumnGrid}>
                        <div></div>
                        <div style={paddingTop20}>Primary Completion Date: {dataInfoRequest?.studyCompletionDate?.substring(0, 10)}</div>
                        <div style={paddingTop20}>
                            <LinkFieldComponent
                                linkTitle={dataInfoRequest?.registryUrl}
                                linkContent={dataInfoRequest?.registryUrl}
                                label={'Clinical Trials: '}
                                labelStyle={{ paddingBottom: '15px' }}
                                style={linkRegistryUrlStyle}
                            />
                        </div>
                        <div>
                            {dataInfoRequest?.isVivliPosted && (
                                <div style={{ paddingTop: '23px' }}>
                                    <a
                                        style={isVivliLinkStyle}
                                        href={transformDoiUrl(getStudyLinkBySponsor(dataInfoRequest.sponsorId))}
                                        target={'_blank'}
                                    >
                                        This Study
                                    </a>
                                    <span style={isVivliStyle}> is listed on the Vivli Platform</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={twoColumnGrid}>
                        <div style={paddingTop35}>
                            {
                                //TODO PERMFE - redo with permissions+
                                !isApprovedState && (isVivliAdmin || (isRequester && !isReadOnlyRequester)) && (
                                    <MultiSelectDropdownFormFieldComponent
                                        label={'Data Requested'}
                                        name={`dataInfoRequests.${dirIndex}.dataType`}
                                        items={dataRequestedMenuItems}
                                        objectKey={'value'}
                                        labelKey={'title'}
                                        arrayKey={'value'}
                                    />
                                )
                            }
                            {(isApprovedState || isReadOnlyRequester) && (
                                <div>
                                    <p>Data Requested:</p>
                                    <ul>
                                        {dataInfoRequest.dataType?.map((x) => (
                                            <li>{DataTypeOptions.find((t) => t.key == x.toString())?.value}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <AdvancedSelectFormFieldComponent
                                name={`dataInfoRequests.${dirIndex}.status`}
                                label={'Response'}
                                items={dirStatusMenuItems}
                                style={marginTop20}
                                hint={<ResponseHint />}
                                widerHint={true}
                                readonly={responseReadOnly || orgAdminReadOnlyState}
                            ></AdvancedSelectFormFieldComponent>
                            <div style={paddingTop20}>{dataInfoRequest.statusString}</div>
                            <AdvancedSelectFormFieldComponent
                                name={`dataInfoRequests.${dirIndex}.notAvailableReason`}
                                label={'Reason'}
                                items={reasonMenuItems}
                                style={marginTop20}
                                hint={<ReasonHint />}
                                widerHint={true}
                                readonly={reasonReadOnly || orgAdminReadOnlyState}
                            ></AdvancedSelectFormFieldComponent>
                        </div>
                        <div>
                            <EnquiryDiscussionGridComponent
                                dirIndex={dirIndex}
                                dataInfoRequest={dataInfoRequest}
                            ></EnquiryDiscussionGridComponent>
                        </div>
                    </div>
                    {
                        //TODO PERMFE - redo with permissions+
                        isAdmin && (
                            <div>
                                <TextAreaFormFieldComponent
                                    name={`dataInfoRequests.${dirIndex}.internalNotes`}
                                    label={'Internal Information'}
                                    readonly={(!isVivliAdmin && !isApprovedState) || orgAdminReadOnlyState}
                                    rows={3}
                                ></TextAreaFormFieldComponent>
                            </div>
                        )
                    }
                    <div style={threeColumnGrid}>
                        <div>Date of Final Response: {dataInfoRequest?.finalResponseDate?.substring(0, 10)} </div>
                        <div>Request Number(s): {dataInfoRequest?.dataRequestsDois} </div>
                    </div>
                </div>
            )}
        </div>
    );
};
